/**
 * archive-events — tiny pub/sub for cross-page cache invalidation.
 *
 * After ANY admin mutation that affects publicly-visible state (dossier
 * publish/unpublish, member edit, newsletter send, source health refresh,
 * etc.), call:
 *
 *    emitArchiveInvalidate("topics")
 *    emitArchiveInvalidate("members")
 *    emitArchiveInvalidate("*")     // refresh everything
 *
 * Any public page that wants live consistency calls:
 *
 *    useArchiveInvalidate("topics", () => refetch())
 *
 * Mechanism: dispatches a CustomEvent on `window`. Cross-tab sync is also
 * supported via the `storage` event so multiple tabs stay in lockstep.
 */
import { useEffect } from "react";

const EVT = "archive:invalidate";
const STORE_KEY = "archive:invalidate:last";

/**
 * Emit an invalidation signal for one or more scopes.
 *
 * Common scopes: "topics", "members", "submissions", "sources", "newsletters",
 * "operative-cards", "audit-log", "*"
 */
export function emitArchiveInvalidate(scope = "*", detail = null) {
  const scopes = Array.isArray(scope) ? scope : [scope];
  const payload = { scopes, detail, ts: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: payload }));
  } catch { /* noop */ }
  try {
    // Cross-tab via storage event.
    window.localStorage.setItem(STORE_KEY, JSON.stringify(payload));
  } catch { /* noop */ }
}

/**
 * Listen for invalidation signals matching `scope`. `scope="*"` matches all.
 *
 * The listener fires WHENEVER an emitter matches OR emits "*".
 */
export function useArchiveInvalidate(scope, handler) {
  useEffect(() => {
    if (!handler) return undefined;
    const scopes = Array.isArray(scope) ? scope : [scope];

    const matches = (incoming) => {
      if (!Array.isArray(incoming)) return false;
      if (incoming.includes("*")) return true;
      if (scopes.includes("*")) return true;
      return incoming.some((s) => scopes.includes(s));
    };

    const onEvt = (e) => {
      const inc = e?.detail?.scopes;
      if (matches(inc)) {
        try { handler(e.detail); } catch { /* noop */ }
      }
    };

    const onStorage = (e) => {
      if (e.key !== STORE_KEY || !e.newValue) return;
      try {
        const payload = JSON.parse(e.newValue);
        if (matches(payload?.scopes)) handler(payload);
      } catch { /* noop */ }
    };

    window.addEventListener(EVT, onEvt);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVT, onEvt);
      window.removeEventListener("storage", onStorage);
    };
  }, [scope, handler]);
}
